import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import zh from './locales/zh.json';
import th from './locales/th.json';
import nl from './locales/nl.json';
import ru from './locales/ru.json';
import ja from './locales/ja.json';
import es from './locales/es.json';
import it from './locales/it.json';
import pt from './locales/pt.json';

const SITE = 'https://siamhorizon.com';
export const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'zh', 'th', 'nl', 'ru', 'ja', 'es', 'it', 'pt'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const isSupported = (code: string | null): code is SupportedLanguage =>
  !!code && (SUPPORTED_LANGUAGES as readonly string[]).includes(code);

/** Language requested through the ?lng= query parameter (used by search engines). */
const getRequestedLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') return 'en';
  const requested = new URLSearchParams(window.location.search).get('lng');
  return isSupported(requested) ? requested : 'en';
};

/** Keeps <html lang>, canonical and og:locale in sync with the active language. */
const syncDocumentLanguage = (lng: string) => {
  if (typeof document === 'undefined') return;
  const lang = isSupported(lng) ? lng : 'en';
  document.documentElement.lang = lang;

  const path = window.location.pathname === '/' ? '/' : window.location.pathname;
  const canonicalHref = lang === 'en' ? `${SITE}${path}` : `${SITE}${path}?lng=${lang}`;

  const links = Array.from(document.querySelectorAll('link[rel="canonical"]')) as HTMLLinkElement[];
  links.slice(1).forEach((l) => l.remove());
  let link = links[0];
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = canonicalHref;

  let ogLocale = document.querySelector('meta[property="og:locale"]') as HTMLMetaElement | null;
  if (!ogLocale) {
    ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    document.head.appendChild(ogLocale);
  }
  ogLocale.content = lang;
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
      zh: { translation: zh },
      th: { translation: th },
      nl: { translation: nl },
      ru: { translation: ru },
      ja: { translation: ja },
      es: { translation: es },
      it: { translation: it },
      pt: { translation: pt },
    },
    lng: getRequestedLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

syncDocumentLanguage(i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

export default i18n;
